import {
  isFromAiLogo,
  isFromLivePPT,
  isFromPcHome,
  isOnlyShowScanUI,
  isTeamSubscription,
  purchaseScene,
  PurchaseScene,
} from '@/constants';
import { TeamInfoResponse } from '@/services/user/index.d';
import {
  PRO_PERIOD_ENTRANCE,
  ProPeriodTabType,
  TabType,
} from '@/constants/subscription';
import {
  apiDefaultTabAtom,
  apiSelectedProductIdAtom,
  businessStrategyIdAtom,
  isFromChannelBusinessAtom,
  SubscriptionEntranceEntries,
} from '@/store/sub';
import {
  curTabAtom,
  hasPurchasedMeidouAtom,
  proPeriodTabAtom,
  subEntranceBizCodeAtom,
  subscriptionEntranceEntriesAtom,
} from '@/store/sub';
import {
  rebateCodeAtom,
  svipRebateCodeAtom,
  inviterUidAtom,
  giftLevelAtom,
  teamInfoAtom,
} from '@/store/user';
import { fetchSubscriptionEntranceEntries } from '@/services/subscription';
import { getIncomeSource, isAppStorePay } from '@/utils/vip';
import { getUrlParam } from '@/utils';
import { getDefaultStore } from 'jotai';
import * as Sentry from '@sentry/react';
import { auditVersionStatusAtom } from '@/store';
import { isProPeriodTabExperiment } from '@/utils/abtest';

/** PC 内嵌兜底（优先级最高） */
const fallbackPcEmbedded = (): SubscriptionEntranceEntries => ({
  subscription: isAppStorePay() ? 'xiuxiu_pro.vip_center' : 'xiuxiu_pro.vip_center_pc_test',
  subscription_ppt_logo: '',
  meidou: 'designkit.meiye',
  cutout: 'sjs_cutout_dangou1',
  svip_1: '',
  svip_2: '',
  svip_3: '',
  svip_4: '',
  batch_process: 'mtxxpc.batch',
});

/** 团队版兜底 */
const fallbackTeam = (): SubscriptionEntranceEntries => ({
  subscription: 'Team_Edition',
  subscription_ppt_logo: '',
  meidou: 'team-meiye',
  cutout: 'sjs_cutout_dangou1',
  svip_1: '',
  svip_2: '',
  svip_3: '',
  svip_4: '',
  batch_process: '',
});

/** 个人版 Web 兜底 */
const fallbackPersonal = (): SubscriptionEntranceEntries => ({
  subscription: isFromPcHome ? 'LivePPT_VIP' : 'sjsweb_vip',
  subscription_ppt_logo: 'LivePPT_VIP',
  meidou: 'designkit.meiye',
  cutout: 'sjs_cutout_dangou1',
  svip_1: 'sjs_Pro_TIER1',
  svip_2: 'sjs_Pro_TIER2',
  svip_3: 'sjs_Pro_TIER3',
  svip_4: 'sjs_Pro_TIER4',
  batch_process: '',
});

/**
 * 接口异常时的兜底 entries（顺序：PC 内嵌 → 团队 → 个人）
 */
export const buildFallbackEntranceEntries = (): SubscriptionEntranceEntries => {
  if (getIncomeSource() !== 'MTXX-WEB') {
    return fallbackPcEmbedded();
  }
  if (isTeamSubscription) {
    return fallbackTeam();
  }
  return fallbackPersonal();
};

/**
 * 请求入口映射并返回；失败时返回 null（由调用方使用兜底）
 */
export const fetchSubscriptionEntranceEntriesSafe =
  async (): Promise<SubscriptionEntranceEntries | null> => {
    try {
      const res = await fetchSubscriptionEntranceEntries();
      const store = getDefaultStore();
      // 渠道策略ID
      store.set(businessStrategyIdAtom, res?.data.data?.id || '');
      // 是否来自渠道策略用户
      store.set(
        isFromChannelBusinessAtom,
        !!res?.data.data?.id && res.data.data.type === 'channel'
      );
      // 分佣码与邀请人（原 /sub_promote/get_rebate_code 已废弃，统一由此接口下发）
      store.set(rebateCodeAtom, res?.data.data?.rebate_code || '');
      store.set(svipRebateCodeAtom, res?.data.data?.svip_rebate_code || '');
      store.set(inviterUidAtom, res?.data.data?.inviter_uid || '');

      const purchased = res?.data.data?.has_purchased_meidou;
      store.set(hasPurchasedMeidouAtom, typeof purchased === 'boolean' ? purchased : null);

      // 只有个人版web(剔除团队版/PC内嵌/PC首页)
      if (!isTeamSubscription && getIncomeSource() === 'MTXX-WEB' && !isFromPcHome) {
        // 接口下发的默认 tab 与默认选中商品 ID（selected 仅在 default_tab 有值时由后端保证有效）
        store.set(apiDefaultTabAtom, res?.data.data?.default_tab || '');
        store.set(apiSelectedProductIdAtom, res?.data.data?.selected || '');
      }
      return (res?.data.data?.entries as SubscriptionEntranceEntries) || null;
    } catch (e) {
      Sentry.captureException(e);
    }
    return null;
  };

/**
 * 拉取各场景入口映射并写入 `subscriptionEntranceEntriesAtom`（与 `get_init_data` 的 `web_sub_entrance` 职责解耦）。
 *
 * 每次调用都会请求接口，以便在 `fetchSubscriptionEntranceEntriesSafe` 内同步更新
 * `businessStrategyIdAtom` / `isFromChannelBusinessAtom`。后者必须在 `initFn` 里先于
 * `checkIsNewUI()`（见 `common.ts` 中 `initCurTabValue`、`isViewNewUiAtom`）写入完成，
 * 不可因 entries 已缓存而跳过请求，否则渠道用户仍可能命中新版订阅实验。
 */
export const loadSubscriptionEntranceEntries = async () => {
  const store = getDefaultStore();

  // 支付二维码弹窗场景无需请求接口，其余场景尝试接口获取
  // 因为价格页的支付二维码固定要走通用的商品入口，不受用户渠道等因素影响下发的商品入口
  const entries = isOnlyShowScanUI ? null : await fetchSubscriptionEntranceEntriesSafe();

  store.set(
    subscriptionEntranceEntriesAtom,
    entries || store.get(subscriptionEntranceEntriesAtom) || buildFallbackEntranceEntries()
  );
};

/** 团队版会员商品入口：vip_level 为 1 且加购席位时固定 team-subscribe，其余读接口 */
export const resolveTeamSubscriptionEntrance = (
  entries: SubscriptionEntranceEntries,
  teamInfo?: TeamInfoResponse['data']
): string => {
  if (teamInfo?.vip_level === 1 && purchaseScene === PurchaseScene.EXTRA) {
    return 'team-subscribe';
  }
  return entries.subscription;
};

export type ResolveSubEntranceParams = {
  entries: SubscriptionEntranceEntries;
  type: string | null | undefined;
  curTab: TabType;
  /** 场景计算前的 atom 值（如邀请兑换下发的 subscribe_entry） */
  subEntranceBizCode: string;
  rebateCode: string;
  giftLevel: number;
};

/**
 * 同步更新入口标识
 */
export const syncSubEntranceBizCode = () => {
  const store = getDefaultStore();

  // isOnlyShowScanUI=1 场景下，URL 指定的 biz_code 优先级最高
  const urlBizCode = getUrlParam('biz_code');
  if (isOnlyShowScanUI && urlBizCode) {
    store.set(subEntranceBizCodeAtom, urlBizCode);
    return;
  }

  const curTab = store.get(curTabAtom);
  const entries = store.get(subscriptionEntranceEntriesAtom);
  const giftLevel = store.get(giftLevelAtom);
  const rebateCode = store.get(rebateCodeAtom);

  if (!entries) {
    return;
  }

  const type = getUrlParam('type');

  // 批处理
  if (type === 'svip') {
    store.set(subEntranceBizCodeAtom, entries.batch_process);
    return;
  }

  // 美豆
  if (curTab === TabType.MEIDOU) {
    store.set(subEntranceBizCodeAtom, entries.meidou);
    return;
  }

  // 抠图
  if (curTab === TabType.CUTOUT) {
    store.set(subEntranceBizCodeAtom, entries.cutout);
    return;
  }

  // 高级会员
  if (curTab === TabType.PRO_SUB) {
    if (isProPeriodTabExperiment()) {
      const period = store.get(proPeriodTabAtom);
      const code =
        period === ProPeriodTabType.ANNUAL
          ? PRO_PERIOD_ENTRANCE[ProPeriodTabType.ANNUAL]
          : PRO_PERIOD_ENTRANCE[ProPeriodTabType.MONTHLY];
      store.set(subEntranceBizCodeAtom, code);
      return;
    }
    const key = `svip_${giftLevel}` as keyof SubscriptionEntranceEntries;
    const v = entries[key];
    store.set(subEntranceBizCodeAtom, v!);
    return;
  }

  // 邀请页打开的优惠购买
  if (
    getIncomeSource() === 'MTXX-WEB' &&
    getUrlParam('isFromInvitePage') === '1' &&
    !isTeamSubscription &&
    curTab === TabType.SUB &&
    rebateCode
  ) {
    store.set(subEntranceBizCodeAtom, 'commission_discounts');
    return;
  }

  // PPT/LOGO
  if (
    !isTeamSubscription &&
    (isFromLivePPT || isFromAiLogo) &&
    getIncomeSource() === 'MTXX-WEB' &&
    curTab === TabType.SUB
  ) {
    store.set(subEntranceBizCodeAtom, entries.subscription_ppt_logo!);
    return;
  }

  // 标准会员（团队版按 vip_level 与购买场景解析入口）
  if (isTeamSubscription && curTab === TabType.SUB) {
    store.set(
      subEntranceBizCodeAtom,
      resolveTeamSubscriptionEntrance(entries, store.get(teamInfoAtom))
    );
    return;
  }

  store.set(subEntranceBizCodeAtom, entries.subscription);
};
